import React from "react";
import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  makeStyles
} from "@material-ui/core";
import AddCircleOutlineOutlinedIcon from "@material-ui/icons/AddCircleOutlineOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import moment from "moment";
import { useAlertDialog } from "@bonliva-crm/ui";
import { ConsultantWorkStatusType } from "@bonliva-crm/shared";
import { ConsultantWorkStatusFragmentFragment } from "@bonliva-crm/graphql";
import { localToImplicit } from "@bonliva-crm/common";
import { useLocale, useSnackbars } from "@hooks";
import { useWorkStatusStore } from "../../work-status/use-work-status-store";
import {
  WorkStatusFormModal,
  WorkStatusFormValues
} from "../../work-status/work-status-form-modal";

type Props = {
  consultantId: string;
};

export function WorkStatusSection({ consultantId }: Props) {
  const classes = useStyles();
  const { locale } = useLocale();
  const { showError, showSuccess } = useSnackbars();
  const alert = useAlertDialog();
  const {
    formDialog,
    useWorkStatuses,
    createWorkStatus,
    updateWorkStatus,
    deleteWorkStatus
  } = useWorkStatusStore();

  const { data } = useWorkStatuses(consultantId);
  const workStatuses = data?.consultantWorkStatuses ?? [];
  const editItem: ConsultantWorkStatusFragmentFragment | undefined =
    formDialog.data?.workStatusId
      ? workStatuses.find((s) => s._id === formDialog.data.workStatusId)
      : undefined;

  async function onSubmit(values: WorkStatusFormValues) {
    try {
      const input = {
        type: values.type,
        startDate: localToImplicit(values.startDate),
        competitorName:
          values.type === ConsultantWorkStatusType.WorkingForCompetitor
            ? (values.competitorName ?? null)
            : null,
        reminderDate: values.reminderDate
          ? localToImplicit(values.reminderDate)
          : null
      };

      if (values.id) {
        await updateWorkStatus({ variables: { id: values.id, input } });
      } else {
        await createWorkStatus({ variables: { consultantId, input } });
      }

      showSuccess(locale.Saved);
      formDialog.onClose();
    } catch (error) {
      showError(error);
    }
  }

  function onDelete(id: string) {
    alert
      .confirm({ title: locale.AreYouSure, description: "" })
      .then((confirmed) => {
        if (!confirmed) return;
        deleteWorkStatus({
          variables: { id },
          onCompleted: () => showSuccess(locale.Saved),
          onError: (error) => showError(error?.message)
        });
      });
  }

  return (
    <>
      <WorkStatusFormModal
        open={formDialog.open}
        onClose={formDialog.onClose}
        onSubmit={onSubmit}
        editItem={editItem}
      />
      <Divider className={classes.divider} />
      <Box className={classes.header}>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classes.sectionTitle}
        >
          {locale.workStatus.title}
        </Typography>
        <IconButton
          size="small"
          color="primary"
          onClick={() => formDialog.onOpen({ workStatusId: undefined })}
        >
          <AddCircleOutlineOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box className={classes.container}>
        {workStatuses.map((item) => (
          <Tooltip
            key={item._id}
            title={`${locale.Created}: ${item.createdByName}, ${moment(item.createdOn).format("YYYY-MM-DD")}`}
            placement="top-start"
            interactive
          >
            <Box className={classes.statusItem}>
              <Box className={classes.statusInfo}>
                <Typography variant="h6" component="div">
                  {
                    locale.workStatus.types[
                      item.type as ConsultantWorkStatusType
                    ]
                  }
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {locale.StartDate}:{" "}
                  {moment(item.startDate).format("YYYY-MM-DD")}
                </Typography>
                {item.reminderDate && (
                  <Typography variant="body2" color="textSecondary">
                    {locale.workStatus.reminderDate}:{" "}
                    {moment(item.reminderDate).format("YYYY-MM-DD")}
                  </Typography>
                )}
                {item.competitorName && (
                  <Typography variant="body2" color="textSecondary">
                    {locale["Works for competitors"]}: {item.competitorName}
                  </Typography>
                )}
              </Box>
              <Box className={classes.statusActions}>
                <IconButton
                  size="small"
                  onClick={() =>
                    formDialog.onOpen({ workStatusId: item._id })
                  }
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(item._id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  divider: {
    margin: "1rem 0"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.5rem"
  },
  sectionTitle: {
    textTransform: "uppercase"
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1)
  },
  statusItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start"
  },
  statusInfo: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.25)
  },
  statusActions: {
    display: "flex",
    gap: theme.spacing(0.25)
  }
}));
